'use client';

/**
 * FilterResults — the Explore / Private `.se-content` results column.
 *
 * Two states (mirrors `design-refs/source/private-home.js`):
 *  - browse view (no active filter): the three `SECTIONS` shelves, each a
 *    `.cat-header` + a horizontal `.scroll-row` of up to four OfferingCards,
 *    separated by `.cat-divider` (source `buildVersionA` browse-view).
 *  - filter view (a sidebar item is active): a `.filter-head` (group eyebrow,
 *    selected label, offering count, "All offerings" back button) above a
 *    `.filter-grid` of the matching offerings — or a `.filter-empty` panel
 *    when nothing matches (source `renderFilterView`).
 *
 * CSS source: design-refs/source/assets/private-home.css (`.cat-*`,
 * `.scroll-row`, `.filter-head`, `.filter-grid`, `.filter-empty`).
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import Skeleton from '@mui/material/Skeleton';
import {
  SECTIONS,
  COMPANIES,
  FILTER_META,
  isOpenToAll,
  type Company,
  type OfferingCardData,
  type OfferingSection,
} from 'components/HomepageV3/data/demoData';
import SectionHeader from 'components/HomepageV3/components/SectionHeader';
import EditorialButton from 'components/HomepageV3/components/EditorialButton';
import OfferingCard from 'components/HomepageV3/components/OfferingCard';
import { useAssignedHomepageTombstoneVariant } from 'components/HomepageV3/experiments/tombstoneExperiment';
import {
  matchCompanies,
  cardMinimum,
  minimumBucketKey,
  type FilterSelection,
  type SidebarView,
} from 'components/HomepageV3/components/FilterSidebar';
import {
  buildSe3ExploreRoute,
  buildSe3ExploreSectionRoute,
  slugify,
  useSe3QaSearch,
} from 'components/HomepageV3/utils/routes';
import { matchIndustryCards } from 'components/HomepageV3/utils/industries';
import {
  applyEligibilityBadgesToSections,
  useEligibilityBadges,
} from 'components/HomepageV3/data/eligibilityBadges';

export interface FilterResultsProps {
  view?: SidebarView;
  /** Active sidebar selection; `null` renders the default browse view. */
  selection?: FilterSelection | null;
  /** Override the source browse shelves. Defaults to demoData SECTIONS. */
  sections?: OfferingSection[];
  /**
   * PD-36468 — uncapped per-category shelves. When provided, a "View all
   * <category>" selection lists every offering in that category instead of the
   * capped `sections` preview. Falls back to `sections` when absent.
   */
  fullSections?: OfferingSection[];
  /** Company lookup keyed by `card.co`. Defaults to demoData COMPANIES. */
  companies?: Record<string, Company>;
  /** Logo base path forwarded to each card. */
  logoBasePath?: string;
  /** Explore's selected industry is already the context; omit repeated card industry lines. */
  hideIndustrySubheads?: boolean;
  /** Search query from the SE3 explore URL/nav search. */
  searchQuery?: string;
  /** Search sections already filtered and ranked by the search service. */
  searchResultsPreFiltered?: boolean;
  /** Live offerings still loading — suppresses the search "No results" flash. */
  isLoading?: boolean;
  /** Incremented by parents after a user-initiated filter change. */
  focusOnUpdateKey?: number;
  /**
   * Browse-view layout. 'shelf' (default, home/explore) = horizontal scroll
   * rows; 'grid' (private) = a wrapping card grid per the private-2 design.
   */
  browseLayout?: 'shelf' | 'grid';
  /** Hide the bottom "View all offerings" bar (explore/private, where it's redundant). */
  showAllOfferingsBar?: boolean;
  /**
   * Optional preview cap for a browse grid. A resolver keeps sections that
   * should be fully expanded (such as private's available offerings) from
   * inheriting a cap intended only for another section.
   */
  browseGridPreviewLimit?: BrowseGridPreviewLimit;
  /** Expand a capped browse grid inline instead of navigating to Explore. */
  expandGridViewAll?: boolean;
  /** Optional content placed beneath the first browse-section heading. */
  browseHeaderAccessory?: ReactNode;
  /**
   * Filter the existing browse sections without replacing their heading or
   * controls. Private uses this for its in-page search so the controlled input
   * stays mounted (and focused) while someone types.
   */
  filterInPlace?: boolean;
  onViewCard?: (card: OfferingCardData) => void;
  /** Suppress card activation for cards the parent cannot route anywhere. */
  canActivateCard?: (card: OfferingCardData) => boolean;
  onTrackCard?: (card: OfferingCardData) => void;
  onClear?: () => void;
  /** Empty-state "Notify me" CTA — opens the signup/login modal. */
  onNotify?: () => void;
  'data-cy'?: string;
}

type BrowseGridPreviewLimit = number | ((section: OfferingSection) => number | undefined);

/** Filter a card list to open-to-all offerings when in `open` view. */
function viewCards(cards: OfferingCardData[], view: SidebarView): OfferingCardData[] {
  return view === 'open' ? cards.filter((c) => isOpenToAll(c.co)) : cards;
}

/**
 * Deduped offerings whose minimum falls in the requested bucket.
 *
 * Prefer the dedicated Low minimum shelf before the general catalog/trending
 * sections. The same offering can occur in all of them, but only the Low
 * minimum card has the intentional Minimum → Invested presentation and its
 * matching authoritative progress fields.
 */
export function cardsForMinimumBucket(
  sections: OfferingSection[],
  companies: Record<string, Company>,
  bucketKey: string,
): OfferingCardData[] {
  const seen = new Set<string>();
  const out: OfferingCardData[] = [];
  const lowMinimum = sections.find((section) => section.title === 'Low minimum offerings');
  const sourceSections = lowMinimum
    ? [lowMinimum, ...sections.filter((section) => section !== lowMinimum)]
    : sections;

  sourceSections.forEach((section) => {
    section.cards.forEach((card) => {
      if (seen.has(card.co)) return;
      const company = companies[card.co];
      if (!company) return;
      const live = (company as { minimumAmount?: number }).minimumAmount;
      const minimum =
        typeof live === 'number' && Number.isFinite(live) && live > 0 ? live : cardMinimum(card);
      const crowd = Boolean(company.regcf || company.regA);
      if (minimumBucketKey(minimum, crowd) !== bucketKey) return;
      seen.add(card.co);
      out.push(card);
    });
  });
  return out;
}

/** Synthesise an offering card from FILTER_META (mirrors source `synthCard`). */
function synthCard(coKey: string): OfferingCardData {
  const m = FILTER_META[coKey];
  return {
    co: coKey as OfferingCardData['co'],
    badge: m.badge,
    figR: { lbl: 'Availability', val: m.avail, cls: m.cls },
    fill: { w: m.fillW, cls: m.cls },
  };
}

function searchCards(
  query: string,
  sections: OfferingSection[],
  companies: Record<string, Company>,
): OfferingCardData[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return [];
  const seen = new Set<string>();
  return sections
    .flatMap((section) => section.cards)
    .filter((card) => {
      if (seen.has(card.co)) return false;
      const company = companies[card.co];
      if (!company) return false;
      const haystack = [
        company.key,
        company.name,
        company.series,
        // Live private cards often render the series vehicle as their display
        // name. Include the issuer/legal company name as well so the in-page
        // search behaves like the global offering search (e.g. "Lambda").
        company.companyName,
        company.sector,
        company.desc,
        company.reg,
        card.co,
        card.desc,
        card.meta,
        ...(card.searchTerms ?? []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const matched = haystack.includes(normalized);
      if (matched) seen.add(card.co);
      return matched;
    });
}

function uniqueCards(sections: OfferingSection[]): OfferingCardData[] {
  const seen = new Set<string>();
  return sections.flatMap((section) =>
    section.cards.filter((card) => {
      if (seen.has(card.co)) return false;
      seen.add(card.co);
      return true;
    }),
  );
}

const SOLD_OUT_CARD_DESCRIPTION_LINES = 7;

function isSoldOutSection(section: OfferingSection): boolean {
  return /sold out|previously closed/i.test(`${section.eyebrow} ${section.title} ${section.desc}`);
}

export function hasClosedStatus(card: OfferingCardData): boolean {
  // Eligibility metadata such as "Previously Sold Out" describes the series'
  // history; it does not mean the current offering is closed. Treating that
  // badge as a closed status made the whole Trending grid opt out of its
  // equal-height stretch layout.
  const statusText = `${card.figR?.lbl ?? ''} ${card.figR?.val ?? ''}`;
  return /closed|fully subscribed|sold out/i.test(statusText);
}

function descriptionClampLines(
  card: OfferingCardData,
  forceSoldOutSection = false,
): number | undefined {
  return forceSoldOutSection || hasClosedStatus(card) ? SOLD_OUT_CARD_DESCRIPTION_LINES : undefined;
}

function cardActivateHandler(
  card: OfferingCardData,
  onViewCard?: (card: OfferingCardData) => void,
  canActivateCard?: (card: OfferingCardData) => boolean,
): (() => void) | undefined {
  if (!onViewCard) return undefined;
  if (canActivateCard && !canActivateCard(card)) return undefined;
  return () => onViewCard(card);
}

const ResultsRoot = styled('div')({
  // Sticky nav is 79px (desktop/tablet) / 67px (mobile); clear it when a filter
  // selection scrolls results into view so the section title stays visible
  // rather than parking under the nav (Paige QA).
  scrollMarginTop: '96px',
  '&:focus': {
    outline: 'none',
  },
  // Variant B keeps the handoff's horizontal marketplace rail, with its 320px
  // fixed tombstones. Do not let `fluid` cards expand to fill the browse area.
  // Scope this to the homepage provider so Explore and Private retain their
  // existing card geometry.
  '&[data-tombstone-variant="marketplace-overlay"] .scroll-row': {
    '& > *': {
      flex: '0 0 320px',
      width: '320px',
    },
  },
  '&[data-tombstone-variant="marketplace-overlay"] .filter-grid': {
    gridTemplateColumns: 'repeat(auto-fill, 320px)',
  },
  '@media (max-width: 640px)': {
    '&[data-tombstone-variant="marketplace-overlay"] .scroll-row': {
      '& > *': {
        flex: '0 0 min(320px, calc(100vw - var(--pad) - var(--pad)))',
        width: 'min(320px, calc(100vw - var(--pad) - var(--pad)))',
      },
    },
    '&[data-tombstone-variant="marketplace-overlay"] .filter-grid': {
      gridTemplateColumns: '1fr',
    },
  },
});

/* ── Browse view ── */

const CatSection = styled('div')({
  marginBottom: '44px',
});

const CatHeader = styled('div')({
  padding: '0 var(--pad)',
  marginBottom: '22px',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  gap: '20px',
});

const CatHeaderMain = styled('div')({
  flex: 1,
  minWidth: 0,
});

const ShelfGrid = styled('div')({
  display: 'flex',
  gap: '20px',
  padding: '4px 0 14px',
  overflowX: 'auto',
  scrollbarWidth: 'none',
  WebkitOverflowScrolling: 'touch',
  '&::-webkit-scrollbar': { display: 'none' },
  // Every tombstone shares the row's cross-axis height. This must also hold
  // for cards with a closed-status description clamp; otherwise that one
  // status creates a shorter card in the same shelf.
  alignItems: 'stretch',
  '& > *': {
    flex: '0 0 296px',
    width: '296px',
  },
  '& > *:first-of-type': { marginLeft: 'var(--pad)' },
  '& > *:last-of-type': { marginRight: 'var(--pad)' },
  '@media (max-width: 640px)': {
    gap: '14px',
  },
});

/** Shared "View all" link typography/color/hover; variants add layout + visibility. */
const viewAllBase = {
  fontFamily: 'var(--sans)',
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase' as const,
  color: 'var(--teal-ink)',
  textDecoration: 'none',
  whiteSpace: 'nowrap' as const,
  cursor: 'pointer',
  '&:hover': { color: 'var(--gold)' },
};

const ViewAll = styled('a')({
  ...viewAllBase,
  alignSelf: 'center',
  marginLeft: 'auto',
  flexShrink: 0,
  padding: '0 8px 0 28px',
  '@media (max-width: 640px)': { display: 'none' },
});

const MobileViewAll = styled('a')({
  ...viewAllBase,
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: '44px',
  width: 'fit-content',
  margin: '-8px var(--pad) 16px',
  '@media (min-width: 641px)': { display: 'none' },
});

const ViewAllButton = styled('button')({
  ...viewAllBase,
  background: 'transparent',
  border: 0,
  alignSelf: 'center',
  marginLeft: 'auto',
  flexShrink: 0,
  padding: '0 8px 0 28px',
  '@media (max-width: 640px)': { display: 'none' },
});

const MobileViewAllButton = styled('button')({
  ...viewAllBase,
  background: 'transparent',
  border: 0,
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: '44px',
  width: 'fit-content',
  margin: '-8px var(--pad) 16px',
  '@media (min-width: 641px)': { display: 'none' },
});

const CatDivider = styled('div')({
  height: '1px',
  background: 'var(--edge)',
  margin: '0 var(--pad) 44px',
  '@media (max-width: 640px)': { margin: '0 var(--pad) 36px' },
});

const AllOfferingsBar = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  padding: '12px var(--pad) 8px',
});

const AllOfferingsLink = styled('a')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  fontFamily: 'var(--sans)',
  fontSize: '14px',
  fontWeight: 600,
  letterSpacing: '0.04em',
  color: 'var(--ink)',
  background: 'transparent',
  border: '1px solid var(--edge)',
  borderRadius: '999px',
  padding: '13px 28px',
  textDecoration: 'none',
  cursor: 'pointer',
  transition: 'border-color 0.15s, background 0.15s',
  '&:hover': { borderColor: 'var(--gold)', background: 'var(--cream-2)' },
});

/* ── Filter view ── */

const FilterHead = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  flexWrap: 'wrap',
  padding: '0 var(--pad)',
  marginBottom: '30px',
});

const FilterHeadMain = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

const FilterEyebrow = styled('span')({
  fontFamily: 'var(--sans)',
  fontSize: '12px',
  fontWeight: 600,
  letterSpacing: '0.32em',
  textTransform: 'uppercase',
  color: 'var(--gold)',
});

const FilterTitle = styled('span')({
  fontFamily: 'var(--serif)',
  fontSize: '32px',
  fontWeight: 400,
  color: 'var(--ink)',
  letterSpacing: '-0.01em',
  lineHeight: 1,
});

const FilterCount = styled('span')({
  marginLeft: 'auto',
  fontFamily: 'var(--sans)',
  fontSize: '14px',
  color: 'var(--ink-2)',
  whiteSpace: 'nowrap',
});

const FilterBack = styled('button')({
  fontFamily: 'var(--sans)',
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: 'var(--cream)',
  background: 'var(--night)',
  border: '1px solid var(--night)',
  borderRadius: '999px',
  padding: '9px 16px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: 'background .18s, color .18s, border-color .18s',
  '&:hover': { background: 'var(--night-2)', borderColor: 'var(--night-2)' },
});

const FilterGrid = styled('div')({
  display: 'grid',
  // Filtered results use the same fixed card track as the horizontal shelves.
  // Do not use `1fr` here: it distributes the remaining width across the
  // selected cards, making a small result set look wider/narrower than the
  // tombstones a visitor just saw on the homepage.
  gridTemplateColumns: 'repeat(auto-fill, 296px)',
  justifyContent: 'start',
  gap: '20px',
  // The browse shelf has a leading --pad gutter but no matching trailing
  // gutter inside its scroll track. Keep the filtered grid's geometry the
  // same: an extra right gutter was enough to make this viewport fit only
  // three 296px cards while the unfiltered shelf showed four.
  padding: '4px 0 0 var(--pad)',
  // Filtered cards use the exact same equal-height behavior as shelf cards.
  alignItems: 'stretch',
  '& > *': { width: '100%' },
  '@media (max-width: 640px)': {
    gridTemplateColumns: '1fr',
    gap: '14px',
    '& > *': {
      width: '100%',
      maxWidth: '420px',
      margin: '0 auto',
    },
  },
});

const FilterEmpty = styled('div')({
  margin: '0 var(--pad)',
  border: '1px solid var(--edge)',
  borderRadius: '20px',
  background: 'var(--card)',
  padding: '56px 44px',
  textAlign: 'center',
  maxWidth: '640px',
});

const FilterEmptyTitle = styled('div')({
  fontFamily: 'var(--serif)',
  fontSize: '26px',
  fontWeight: 400,
  color: 'var(--ink)',
  letterSpacing: '-0.01em',
  marginBottom: '12px',
});

const FilterEmptyText = styled('p')({
  fontFamily: 'var(--sans)',
  fontSize: '14px',
  color: 'var(--ink-2)',
  lineHeight: 1.62,
  maxWidth: '420px',
  margin: '0 auto 24px',
});

const SkeletonCard = styled('div')({
  flex: '0 0 296px',
  width: '296px',
  border: '1px solid var(--edge)',
  borderRadius: '16px',
  overflow: 'hidden',
  background: 'var(--card)',
});

/**
 * BrowseSkeleton — shelf-shaped loading placeholder shown while live offerings
 * load, in place of the demo `SECTIONS` cards that used to flash in as "relic"
 * tombstones before real data arrived (QA). Mirrors the browse shelf layout:
 * a header + a horizontal row of card skeletons, for two shelves.
 */
function BrowseSkeleton() {
  return (
    <div className="browse-view" data-cy="explore-browse-skeleton">
      {[0, 1].map((shelf) => (
        <CatSection key={shelf} className="cat-section">
          <CatHeader className="cat-header">
            <CatHeaderMain>
              <Skeleton variant="text" width={120} height={16} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" width={220} height={32} />
            </CatHeaderMain>
          </CatHeader>
          <ShelfGrid className="scroll-row">
            {[0, 1, 2, 4].map((card) => (
              <SkeletonCard key={card}>
                <Skeleton variant="rectangular" width="100%" height={180} />
                <div style={{ padding: '16px' }}>
                  <Skeleton variant="text" width="70%" height={22} sx={{ mb: 0.5 }} />
                  <Skeleton variant="text" width="100%" height={14} />
                  <Skeleton variant="text" width="85%" height={14} sx={{ mb: 1.5 }} />
                  <Skeleton variant="rounded" width="100%" height={8} sx={{ mb: 1.5 }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Skeleton variant="text" width="35%" height={18} />
                    <Skeleton variant="text" width="25%" height={18} />
                  </div>
                </div>
              </SkeletonCard>
            ))}
          </ShelfGrid>
        </CatSection>
      ))}
    </div>
  );
}

// Per-shelf preview caps keyed by shelf TITLE (position-independent — an empty
// shelf must not shift another shelf's cap). The homepage shows only four cards
// in each default row; View all / a sidebar selection receives fullSections and
// therefore retains the complete independently ordered catalog.
const SHELF_PREVIEW_LIMITS: Record<string, number> = {
  'Trending now': 4,
  'Closing soon': 4,
  'Low minimum offerings': 4,
};

function BrowseView({
  view,
  sections,
  companies,
  logoBasePath,
  hideIndustrySubheads = false,
  onViewCard,
  canActivateCard,
  onTrackCard,
  browseLayout = 'shelf',
  showAllOfferingsBar = true,
  browseGridPreviewLimit,
  expandGridViewAll = false,
  browseHeaderAccessory,
  filterInPlace = false,
  hasActiveQuery = false,
}: {
  view: SidebarView;
  sections: OfferingSection[];
  companies: Record<string, Company>;
  logoBasePath?: string;
  hideIndustrySubheads?: boolean;
  onViewCard?: (card: OfferingCardData) => void;
  /** Suppress card activation for cards the parent cannot route anywhere. */
  canActivateCard?: (card: OfferingCardData) => boolean;
  onTrackCard?: (card: OfferingCardData) => void;
  browseLayout?: 'shelf' | 'grid';
  showAllOfferingsBar?: boolean;
  browseGridPreviewLimit?: BrowseGridPreviewLimit;
  expandGridViewAll?: boolean;
  browseHeaderAccessory?: ReactNode;
  filterInPlace?: boolean;
  hasActiveQuery?: boolean;
}) {
  const qaSearch = useSe3QaSearch();
  const [expandedGridSections, setExpandedGridSections] = useState<Record<string, boolean>>({});
  const mappedSections = sections.map((s) => ({ section: s, cards: viewCards(s.cards, view) }));
  // filterInPlace search (private) keeps the FIRST section mounted even when a
  // query matches nothing, so the stable heading + search input never unmount
  // mid-query (which stole focus). Everything renders through the single map
  // below — the empty state swaps in only *beneath* the persistent header, so
  // React reconciles the input node instead of remounting it.
  const visibleSections =
    filterInPlace && hasActiveQuery
      ? mappedSections.filter((o, idx) => o.cards.length > 0 || idx === 0)
      : mappedSections.filter((o) => o.cards.length > 0);

  return (
    <div className="browse-view" data-cy="explore-browse-view">
      {visibleSections.map(({ section, cards }, i) => {
        const slug = slugify(section.title);
        const viewAllHref = buildSe3ExploreSectionRoute(slug, qaSearch);
        const viewAllLabel = `View all ${section.title}`;
        const compactCards = isSoldOutSection(section);
        const resolvedBrowseGridPreviewLimit =
          typeof browseGridPreviewLimit === 'function'
            ? browseGridPreviewLimit(section)
            : browseGridPreviewLimit;
        const gridPreviewLimit =
          browseLayout === 'grid' &&
          expandGridViewAll &&
          typeof resolvedBrowseGridPreviewLimit === 'number'
            ? resolvedBrowseGridPreviewLimit
            : undefined;
        const canExpandGrid = Boolean(gridPreviewLimit && cards.length > gridPreviewLimit);
        const isGridExpanded = expandedGridSections[slug] === true;
        const showInlineGridViewAll = canExpandGrid && !isGridExpanded;
        const visibleCards =
          gridPreviewLimit && !isGridExpanded ? cards.slice(0, gridPreviewLimit) : cards;
        const expandGrid = () => {
          setExpandedGridSections((previous) => ({ ...previous, [slug]: true }));
        };
        return (
          <div key={section.title}>
            <CatSection className="cat-section" data-cy={`explore-cat-${slug}`}>
              <CatHeader className="cat-header">
                <CatHeaderMain>
                  <SectionHeader
                    eyebrow={section.eyebrow}
                    title={section.title}
                    desc={section.desc}
                    tip={section.tip}
                    data-cy={`explore-cat-header-${slug}`}
                  />
                </CatHeaderMain>
                {canExpandGrid && showInlineGridViewAll && (
                  <ViewAllButton
                    type="button"
                    className="cat-viewall"
                    aria-label={viewAllLabel}
                    aria-expanded={false}
                    onClick={expandGrid}
                    data-cy={`explore-cat-viewall-expand-desktop-${slug}`}
                  >
                    View all →
                  </ViewAllButton>
                )}
                {!canExpandGrid && browseLayout !== 'grid' && (
                  <ViewAll
                    className="cat-viewall"
                    href={viewAllHref}
                    aria-label={viewAllLabel}
                    data-cy={`explore-cat-viewall-desktop-${slug}`}
                  >
                    View all →
                  </ViewAll>
                )}
              </CatHeader>
              {i === 0 && browseHeaderAccessory}
              {cards.length === 0 ? (
                <FilterEmpty className="filter-empty" data-cy="explore-search-empty">
                  <FilterEmptyTitle className="filter-empty-title">
                    No matching offerings
                  </FilterEmptyTitle>
                  <FilterEmptyText>Try a company name, industry, or offering type.</FilterEmptyText>
                </FilterEmpty>
              ) : (
                <>
                  {canExpandGrid && showInlineGridViewAll && (
                    <MobileViewAllButton
                      type="button"
                      className="cat-viewall-mobile"
                      aria-label={viewAllLabel}
                      aria-expanded={false}
                      onClick={expandGrid}
                      data-cy={`explore-cat-viewall-expand-mobile-${slug}`}
                    >
                      View all →
                    </MobileViewAllButton>
                  )}
                  {!canExpandGrid && browseLayout !== 'grid' && (
                    <MobileViewAll
                      className="cat-viewall-mobile"
                      href={viewAllHref}
                      aria-label={viewAllLabel}
                      data-cy={`explore-cat-viewall-mobile-${slug}`}
                    >
                      View all →
                    </MobileViewAll>
                  )}
                  {(() => {
                    // Grid mode (private): a wrapping grid showing ALL cards. Shelf
                    // mode (home/explore): a horizontal scroll row capped per shelf.
                    const cardEls = (
                      browseLayout === 'grid'
                        ? visibleCards
                        : (() => {
                            const limit = SHELF_PREVIEW_LIMITS[section.title];
                            return typeof limit === 'number' ? cards.slice(0, limit) : cards;
                          })()
                    ).map((card, idx) => (
                      <OfferingCard
                        key={`${card.co}-${idx}`}
                        card={card}
                        company={companies[card.co]}
                        variant="light"
                        fluid
                        descriptionClampLines={descriptionClampLines(card, compactCards)}
                        disclosure={card.disclosure}
                        logoBasePath={logoBasePath}
                        onCardActivate={cardActivateHandler(card, onViewCard, canActivateCard)}
                        onTrack={onTrackCard ? () => onTrackCard(card) : undefined}
                        data-cy={`explore-card-${slug}-${card.co}`}
                      />
                    ));
                    return browseLayout === 'grid' ? (
                      <FilterGrid data-cy={`explore-grid-${slug}`}>{cardEls}</FilterGrid>
                    ) : (
                      <ShelfGrid className="scroll-row" data-cy={`explore-scroll-${slug}`}>
                        {cardEls}
                      </ShelfGrid>
                    );
                  })()}
                </>
              )}
            </CatSection>
            {i < visibleSections.length - 1 && <CatDivider className="cat-divider" />}
          </div>
        );
      })}
      {visibleSections.length > 0 && showAllOfferingsBar && browseLayout !== 'grid' && (
        <AllOfferingsBar data-cy="browse-all-offerings-bar">
          <AllOfferingsLink
            href={buildSe3ExploreRoute(undefined, qaSearch)}
            data-cy="browse-all-offerings"
          >
            View all offerings →
          </AllOfferingsLink>
        </AllOfferingsBar>
      )}
    </div>
  );
}

function FilterView({
  view,
  selection,
  sections,
  companies,
  logoBasePath,
  hideIndustrySubheads = false,
  isLoading = false,
  onViewCard,
  canActivateCard,
  onTrackCard,
  onClear,
  onNotify,
}: {
  view: SidebarView;
  selection: FilterSelection;
  sections: OfferingSection[];
  companies: Record<string, Company>;
  logoBasePath?: string;
  hideIndustrySubheads?: boolean;
  isLoading?: boolean;
  onViewCard?: (card: OfferingCardData) => void;
  /** Suppress card activation for cards the parent cannot route anywhere. */
  canActivateCard?: (card: OfferingCardData) => boolean;
  onTrackCard?: (card: OfferingCardData) => void;
  onClear?: () => void;
  onNotify?: () => void;
}) {
  const { group, item } = selection;
  let cards: OfferingCardData[];
  let compactCards = false;
  if (item.type === 'browse') {
    const section = sections.find((s) => s.title === item.key);
    cards = section ? section.cards : [];
    compactCards = section ? isSoldOutSection(section) : false;
  } else if (item.type === 'min') {
    cards = cardsForMinimumBucket(sections, companies, item.key);
  } else if (item.type === 'liveInd') {
    // Explore live industry: match the offering pool by category name
    // (Paige QA batch 3), not the demoData FILTER_META synth path.
    cards = matchIndustryCards(
      sections.flatMap((s) => s.cards),
      item.key,
    );
  } else {
    cards = matchCompanies(item.type, item.key, view).map(synthCard);
  }
  cards = viewCards(cards, view);
  compactCards = compactCards || cards.some(hasClosedStatus);
  const n = cards.length;
  const showLoading = n === 0 && isLoading;

  let body: ReactNode;
  if (showLoading) {
    body = (
      <FilterEmpty className="filter-empty" data-cy="explore-filter-loading">
        <FilterEmptyTitle className="filter-empty-title">
          Loading {item.label} offerings…
        </FilterEmptyTitle>
      </FilterEmpty>
    );
  } else if (n === 0) {
    body = (
      <FilterEmpty className="filter-empty" data-cy="explore-filter-empty">
        <FilterEmptyTitle className="filter-empty-title">
          No live {item.label} offerings right now
        </FilterEmptyTitle>
        <FilterEmptyText>
          New offerings in this category are added regularly. Create a free account to get notified
          the moment one opens.
        </FilterEmptyText>
        <EditorialButton variant="solid" onClick={onNotify} data-cy="explore-filter-notify">
          Notify me
        </EditorialButton>
      </FilterEmpty>
    );
  } else {
    body = (
      <FilterGrid className="filter-grid" data-cy="explore-filter-grid">
        {cards.map((card, idx) => (
          <OfferingCard
            key={`${card.co}-${idx}`}
            card={card}
            company={companies[card.co]}
            variant="light"
            fluid
            descriptionClampLines={descriptionClampLines(card, compactCards)}
            disclosure={card.disclosure}
            logoBasePath={logoBasePath}
            onCardActivate={cardActivateHandler(card, onViewCard, canActivateCard)}
            onTrack={onTrackCard ? () => onTrackCard(card) : undefined}
            data-cy={`explore-filter-card-${card.co}`}
          />
        ))}
      </FilterGrid>
    );
  }

  return (
    <div className="filter-view" data-cy="explore-filter-view">
      <FilterHead className="filter-head">
        <FilterHeadMain className="filter-head-main">
          <FilterEyebrow className="cat-eyebrow">{group}</FilterEyebrow>
          <FilterTitle className="cat-title" data-cy="explore-filter-title">
            {item.label}
          </FilterTitle>
        </FilterHeadMain>
        <FilterCount className="filter-count" data-cy="explore-filter-count">
          {n} {n === 1 ? 'offering' : 'offerings'}
        </FilterCount>
        <FilterBack
          className="filter-back"
          type="button"
          onClick={onClear}
          data-cy="explore-filter-back"
        >
          ← All offerings
        </FilterBack>
      </FilterHead>

      {body}
    </div>
  );
}

function SearchView({
  query,
  view,
  sections,
  companies,
  logoBasePath,
  hideIndustrySubheads = false,
  isLoading,
  searchResultsPreFiltered,
  headerAccessory,
  onViewCard,
  canActivateCard,
  onTrackCard,
}: {
  query: string;
  view: SidebarView;
  sections: OfferingSection[];
  companies: Record<string, Company>;
  logoBasePath?: string;
  hideIndustrySubheads?: boolean;
  isLoading?: boolean;
  searchResultsPreFiltered?: boolean;
  /** Optional persistent control rendered above search results (e.g. Private's in-page search). */
  headerAccessory?: ReactNode;
  onViewCard?: (card: OfferingCardData) => void;
  /** Suppress card activation for cards the parent cannot route anywhere. */
  canActivateCard?: (card: OfferingCardData) => boolean;
  onTrackCard?: (card: OfferingCardData) => void;
}) {
  const sourceCards = searchResultsPreFiltered
    ? uniqueCards(sections)
    : searchCards(query, sections, companies);
  const cards = viewCards(sourceCards, view);
  const n = cards.length;
  const compactCards = cards.some(hasClosedStatus);
  // While live offerings load with no rows yet, hold the loading state rather
  // than flashing a false "No results" before the request resolves.
  const showLoading = n === 0 && Boolean(isLoading);

  let body: ReactNode;
  if (showLoading) {
    body = (
      <FilterEmpty className="filter-empty" data-cy="explore-search-loading">
        <FilterEmptyTitle className="filter-empty-title">Searching…</FilterEmptyTitle>
        <FilterEmptyText>Loading offerings for “{query}”.</FilterEmptyText>
      </FilterEmpty>
    );
  } else if (n === 0) {
    body = (
      <FilterEmpty className="filter-empty" data-cy="explore-search-empty">
        <FilterEmptyTitle className="filter-empty-title">No results for “{query}”</FilterEmptyTitle>
        <FilterEmptyText>
          Try a company name, industry, or offering type from the available private-market shelves.
        </FilterEmptyText>
      </FilterEmpty>
    );
  } else {
    body = (
      <FilterGrid className="filter-grid" data-cy="explore-search-grid">
        {cards.map((card, idx) => (
          <OfferingCard
            key={`${card.co}-${idx}`}
            card={card}
            company={companies[card.co]}
            variant="light"
            fluid
            descriptionClampLines={descriptionClampLines(card, compactCards)}
            disclosure={card.disclosure}
            logoBasePath={logoBasePath}
            onCardActivate={cardActivateHandler(card, onViewCard, canActivateCard)}
            onTrack={onTrackCard ? () => onTrackCard(card) : undefined}
            data-cy={`explore-search-card-${card.co}`}
          />
        ))}
      </FilterGrid>
    );
  }

  return (
    <div className="filter-view" data-cy="explore-search-view">
      <FilterHead className="filter-head">
        <FilterHeadMain className="filter-head-main">
          <FilterEyebrow className="cat-eyebrow">Search</FilterEyebrow>
          <FilterTitle className="cat-title" data-cy="explore-search-title">
            {query}
          </FilterTitle>
        </FilterHeadMain>
        <FilterCount className="filter-count" data-cy="explore-search-count">
          {showLoading ? 'Searching…' : `${n} ${n === 1 ? 'offering' : 'offerings'}`}
        </FilterCount>
      </FilterHead>
      {headerAccessory}
      {body}
    </div>
  );
}

function FilterResults({
  view = 'private',
  selection = null,
  sections = SECTIONS,
  fullSections,
  companies = COMPANIES,
  logoBasePath,
  hideIndustrySubheads = false,
  searchQuery = '',
  searchResultsPreFiltered = false,
  isLoading = false,
  focusOnUpdateKey = 0,
  browseLayout = 'shelf',
  showAllOfferingsBar = true,
  browseGridPreviewLimit,
  expandGridViewAll = false,
  browseHeaderAccessory,
  filterInPlace = false,
  onViewCard,
  canActivateCard,
  onTrackCard,
  onClear,
  onNotify,
  'data-cy': dataCy = 'filter-results',
}: FilterResultsProps) {
  const tombstoneVariant = useAssignedHomepageTombstoneVariant();
  const eligibilityBadges = useEligibilityBadges();
  // Keep the PostHog-configured body labels in one shared path. This covers
  // browse shelves, filter grids, search results, and the static fallback on
  // Home, Explore, and Private without changing any card's figures or meter.
  const configuredSections = useMemo(
    () => applyEligibilityBadgesToSections(sections, eligibilityBadges),
    [sections, eligibilityBadges],
  );
  const configuredFullSections = useMemo(
    () =>
      fullSections ? applyEligibilityBadgesToSections(fullSections, eligibilityBadges) : undefined,
    [fullSections, eligibilityBadges],
  );
  const query = searchQuery.trim();
  const inPlaceSearchSections = useMemo(() => {
    if (!filterInPlace || !query) return configuredSections;
    // Private is a single search surface, even though its unfiltered browse
    // view is grouped into sections. Keep results beneath the original first
    // heading so the Open Offerings copy and controlled input never move (or
    // unmount) as a query starts matching cards from a different section.
    const matchingCards = searchCards(query, configuredSections, companies);
    return configuredSections.map((section, index) => ({
      ...section,
      cards: index === 0 ? matchingCards : [],
    }));
  }, [companies, configuredSections, filterInPlace, query]);
  const resultsRef = useRef<HTMLDivElement | null>(null);
  const hasMountedRef = useRef(false);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return undefined;
    }
    if (!focusOnUpdateKey) return undefined;

    const target = resultsRef.current;
    if (!target) return undefined;

    const focusResults = () => {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      target.focus({ preventScroll: true });
    };

    if (typeof window.requestAnimationFrame === 'function') {
      const frame = window.requestAnimationFrame(focusResults);
      return () => window.cancelAnimationFrame(frame);
    }

    const timeout = window.setTimeout(focusResults, 0);
    return () => window.clearTimeout(timeout);
  }, [focusOnUpdateKey]);

  let resultsLabel = 'All offerings';
  if (query && !filterInPlace) {
    resultsLabel = `Search results for ${query}`;
  } else if (selection) {
    resultsLabel = `${selection.item.label} offerings`;
  }

  let content: ReactNode;
  if (query && !filterInPlace) {
    content = (
      <SearchView
        query={query}
        view={view}
        sections={inPlaceSearchSections}
        companies={companies}
        logoBasePath={logoBasePath}
        hideIndustrySubheads={hideIndustrySubheads}
        isLoading={isLoading}
        searchResultsPreFiltered={searchResultsPreFiltered}
        headerAccessory={browseHeaderAccessory}
        onViewCard={onViewCard}
        canActivateCard={canActivateCard}
        onTrackCard={onTrackCard}
      />
    );
  } else if (selection) {
    content = (
      <FilterView
        view={view}
        selection={selection}
        sections={configuredFullSections ?? configuredSections}
        companies={companies}
        logoBasePath={logoBasePath}
        hideIndustrySubheads={hideIndustrySubheads}
        isLoading={isLoading}
        onViewCard={onViewCard}
        canActivateCard={canActivateCard}
        onTrackCard={onTrackCard}
        onClear={onClear}
        onNotify={onNotify}
      />
    );
  } else if (isLoading) {
    // While live offerings load, show shelf-shaped skeletons rather than letting
    // the demo `SECTIONS` fallback flash in as relic tombstones (QA).
    content = <BrowseSkeleton />;
  } else {
    content = (
      <BrowseView
        view={view}
        // The default homepage uses the four-card, cross-shelf-deduped preview.
        // A sidebar/View all selection switches to fullSections above, where an
        // offering may correctly appear in more than one independently ordered row.
        sections={inPlaceSearchSections}
        companies={companies}
        logoBasePath={logoBasePath}
        hideIndustrySubheads={hideIndustrySubheads}
        browseLayout={browseLayout}
        showAllOfferingsBar={showAllOfferingsBar}
        browseGridPreviewLimit={browseGridPreviewLimit}
        expandGridViewAll={expandGridViewAll}
        browseHeaderAccessory={browseHeaderAccessory}
        filterInPlace={filterInPlace}
        hasActiveQuery={Boolean(query)}
        onViewCard={onViewCard}
        canActivateCard={canActivateCard}
        onTrackCard={onTrackCard}
      />
    );
  }

  return (
    <ResultsRoot
      ref={resultsRef}
      role="region"
      aria-label={resultsLabel}
      tabIndex={-1}
      data-cy={dataCy}
      data-tombstone-variant={tombstoneVariant}
    >
      {content}
    </ResultsRoot>
  );
}

export default FilterResults;
